import { useEffect, useRef, useState } from 'react'
import { Bell, CheckCheck } from 'lucide-react'
import { shellNavigate } from '../../../lib/navigation'
import { useMarkRead, useNotices, useUnreadCount } from './data'
import { appLabel, linkTarget, unreadLabel, whenWords, type Notice } from './words'
import './notifications.css'

// The bell on Nateva's front-door top bar (the apps carry the same bell from the kit's <nateva-topbar>).
// It shows the unread count, opens the latest notices, marks them read and links to the Notifications
// page. The launcher's top bar renders it (surface-apps.nateva.ts exports NotificationBell). Without a
// notifications database (older databases) there is no bell at all. While an admin is seeing it as
// someone else the bell shows that person's notices and nothing is marked read.

export function open(link: string | null) {
  const t = linkTarget(link)
  if (t.kind === 'path') shellNavigate(t.path)
  else if (t.kind === 'url') window.location.assign(t.url)
  else shellNavigate('/notifications')
}

export function NotificationBell() {
  const unread = useUnreadCount()
  const [shown, setShown] = useState(false)
  const latest = useNotices({ limit: 8, enabled: shown })
  const { some, all } = useMarkRead()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shown) return
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setShown(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShown(false) }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey) }
  }, [shown])

  // Not switched on in this database, or not signed in yet: no bell.
  if (unread.data === null || unread.data === undefined) return null
  const n = unread.data
  const label = n ? `Notifications, ${n} unread` : 'Notifications'

  const go = (item: Notice) => {
    if (!item.read_at) some.mutate([item.id])
    setShown(false)
    open(item.link)
  }

  return (
    <div ref={ref} className="nb">
      <button
        type="button"
        className="nb-btn"
        aria-haspopup="dialog"
        aria-expanded={shown}
        aria-label={label}
        title="Notifications"
        onClick={() => setShown((v) => !v)}
      >
        <Bell size={18} strokeWidth={1.9} aria-hidden />
        {n > 0 && <span className="nb-count" aria-hidden>{n > 99 ? '99+' : n}</span>}
      </button>
      {shown && (
        <div className="nl-menu nb-menu" role="dialog" aria-label="Notifications">
          <div className="nb-head">
            <p className="nb-title">Notifications</p>
            <span className="nb-sub">{unreadLabel(n)}</span>
            <button type="button" className="nb-all" disabled={n === 0 || all.isPending} onClick={() => all.mutate(null)}>
              <CheckCheck size={15} aria-hidden /> Mark all read
            </button>
          </div>
          {latest.isPending ? (
            <p className="nb-note" aria-busy="true">Loading your notices.</p>
          ) : latest.isError ? (
            <p className="nb-note" role="alert">Your notices could not load just now. Try again in a minute.</p>
          ) : (latest.data ?? []).length === 0 ? (
            <p className="nb-note">Nothing here yet. Notices about your agreements, deals, notes and reports land here.</p>
          ) : (
            <ul className="nb-list">
              {(latest.data ?? []).map((item) => (
                <li key={item.id}>
                  <button type="button" className={`nb-item${item.read_at ? '' : ' is-unread'}`} onClick={() => go(item)}>
                    <span className="nb-dot" aria-hidden />
                    <span className="nb-text">
                      <span className="nb-name">{item.title}</span>
                      {item.body && <span className="nb-body">{item.body}</span>}
                      <span className="nb-meta">{appLabel(item.app)} &middot; {whenWords(item.created_at)}{item.read_at ? '' : ' · unread'}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="nb-foot" onClick={() => { setShown(false); shellNavigate('/notifications') }}>
            See all notifications and settings
          </button>
        </div>
      )}
    </div>
  )
}
